import { Skeleton, Box, useMediaQuery } from '@mui/material';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { ChartData, ChartSeries } from './chart-data';
import { ChartVariant, MOBILE_CHART_HEIGHT, CHART_HEIGHT } from './chart-style';

const BarChartType = lazy(() => import('./BarChartType'));
const LineChartType = lazy(() => import('./LineChartType'));
const PieChartType = lazy(() => import('./PieChartType'));
const AvgMaxMinChartType = lazy(() => import('./AvgMaxMinChartType'));

const Chart = ({
  data,
  variant = ChartVariant.DEFAULT,
  isStacked = false,
}: {
  data?: ChartData;
  variant?: ChartVariant;
  isStacked?: boolean;
}) => {
  const [chartPeriodIndex, setChartPeriodIndex] = useState<number>(0);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);

  const [offsetWidth, setOffsetWidth] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : CHART_HEIGHT;

  const elementRef = useRef(null);
  useEffect(() => {
    setChartPeriodIndex(0);
  }, [data]);

  // useEffect to set the offsetWidth once it's available
  useEffect(() => {
    const currentElement = elementRef?.current as any;
    if (currentElement) {
      setOffsetWidth(currentElement.offsetWidth);
    }
  }, [elementRef]);

  const selectedPeriod = data?.periods[chartPeriodIndex];
  const validSeries = selectedPeriod?.multiSeries
    ? selectedPeriod?.multiSeries
        .filter((chartSeries: ChartSeries) => chartSeries.data && chartSeries.data.length > 0)
        .map((current: ChartSeries) => ({
          name: current.title,
          data: current.data ?? [],
        }))
    : [];

  const handleLegendSelect = (e: any) => {
    const id = e.payload?.id ?? e.id;
    if (hiddenSeries.includes(id)) {
      setHiddenSeries((prev) => prev.filter((series) => series !== id));
    } else {
      const updatedHiddenSeries = [...hiddenSeries];
      updatedHiddenSeries.push(id);
      setHiddenSeries(updatedHiddenSeries);
    }
  };

  const chartType = data?.type;
  return (
    <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={chartHeight} />}>
      <Box style={{ width: '100%', height: 'fit-content' }}>
        {selectedPeriod ? (
          <Box ref={elementRef} sx={{ width: '100%', height: '100%', top: 0, left: 0 }}>
            {chartType === 'Bar' ? (
              <BarChartType
                validSeries={validSeries}
                variant={variant}
                isStacked={isStacked}
                subText={data.subText}
                axisLabelX={data.axisLabelX}
                axisLabelY={data.axisLabelY}
                layout={(data?.layout?.length ?? 0) > 0 ? data.layout : undefined}
              />
            ) : chartType === 'Pie' ? (
              offsetWidth !== null && (
                <PieChartType
                  subTitle={data.subTitle ?? ''}
                  subText={data.subText ?? ''}
                  validSeries={validSeries}
                  variant={variant}
                  width={(elementRef?.current as any)?.offsetWidth ?? chartHeight}
                  height={chartHeight}
                  hiddenSeries={hiddenSeries}
                  onLegendSelect={handleLegendSelect}
                />
              )
            ) : chartType === 'Line' ? (
              <LineChartType
                validSeries={validSeries}
                hiddenSeries={hiddenSeries}
                variant={variant}
                height={chartHeight}
                onLegendSelect={handleLegendSelect}
              />
            ) : chartType === 'AvgMaxMin' ? (
              <AvgMaxMinChartType data={data} />
            ) : null}
          </Box>
        ) : (
          <Skeleton variant="rectangular" width="100%" height={chartHeight} />
        )}
      </Box>
    </Suspense>
  );
};

export default Chart;
